import React from "react";
import type { HistoryItem } from "../hooks/useHistory";

interface HistoryPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  history: HistoryItem[];
  onSelectItem: (question: string) => void;
  onRemoveItem: (id: string) => void;
  onClearHistory: () => void;
}

export function HistoryPanel({
  isOpen,
  onToggle,
  history,
  onSelectItem,
  onRemoveItem,
  onClearHistory,
}: HistoryPanelProps) {
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "A l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        className="history-toggle"
        onClick={onToggle}
        aria-label={isOpen ? "Fermer l'historique" : "Ouvrir l'historique"}
        aria-expanded={isOpen}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      </button>

      {/* Overlay */}
      {isOpen && <div className="history-overlay" onClick={onToggle} />}

      {/* Panel */}
      <aside className={`history-panel ${isOpen ? "is-open" : ""}`}>
        <div className="history-header">
          <h3 className="history-title">Historique</h3>
          <button className="history-close" onClick={onToggle} aria-label="Fermer">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="history-content">
          {history.length === 0 ? (
            <p className="history-empty">Aucune recherche recente.</p>
          ) : (
            <ul className="history-list">
              {history.map((item) => (
                <li key={item.id} className="history-item">
                  <button
                    className="history-item-content"
                    onClick={() => onSelectItem(item.question)}
                  >
                    <span className="history-item-question">{item.question}</span>
                    <span className="history-item-time">{formatDate(item.timestamp)}</span>
                  </button>
                  <button
                    className="history-item-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveItem(item.id);
                    }}
                    aria-label="Supprimer"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {history.length > 0 && (
          <div className="history-footer">
            <button className="history-clear" onClick={onClearHistory}>
              Effacer l'historique
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
